import React, { useEffect, useState, useRef } from "react"; // Import useRef
import axios from "axios";
import { useParams } from "react-router-dom";
import Header from "./header"; // Keep Header and Footer imports
import Footer from "./Footer"; // Keep Header and Footer imports
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Helmet } from "react-helmet";

const API_URL = `${process.env.REACT_APP_API_URL}/get-invoice`;

const styles = {
  page: {
    background: "#f8fafd",
    padding: "40px 0",
    fontFamily: "'Roboto', sans-serif",
    color: "#34495e",
    minHeight: "100vh",
    boxSizing: "border-box",
  },
  card: {
    maxWidth: "1100px",
    margin: "auto",
    background: "#ffffff",
    padding: "40px 50px",
    borderRadius: "16px",
    boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    borderBottom: "2px solid #ecf0f1",
    paddingBottom: "20px",
  },
  logo: {
    width: "220px",
    filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))",
  },
  invoiceInfo: {
    textAlign: "right",
    fontSize: "14px",
    lineHeight: "1.6",
  },
  invoiceId: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#2c3e50",
    marginBottom: "5px",
  },
  invoiceDate: {
    color: "#7f8c8d",
  },
  addressRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "40px",
  },
  addressBlock: {
    background: "#fdfefe",
    padding: "20px 25px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    width: "48%",
  },
  addressTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#2c3e50",
    marginBottom: "10px",
    borderBottom: "1px solid #ecf0f1",
    paddingBottom: "8px",
  },
  addressText: {
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#5f7c8d",
  },
  strongText: {
    fontWeight: 700,
    color: "#2c3e50",
  },
  tableWrapper: {
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "30px",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    marginBottom: "30px",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 5px 20px rgba(0,0,0,0.05)",
  },
  th: {
    padding: "15px 20px",
    backgroundColor: "#eaf2f8",
    color: "#2c3e50",
    fontWeight: 700,
    textAlign: "left",
    borderBottom: "2px solid #dcdcdc",
    fontSize: "15px",
  },
  td: {
    padding: "15px 20px",
    color: "#34495e",
    borderBottom: "1px solid #ecf0f1",
    fontSize: "14px",
  },
  tdRight: {
    textAlign: "right",
  },
  tdCenter: {
    textAlign: "center",
  },
  productName: {
    fontWeight: 600,
    color: "#2c3e50",
  },
  tbodyRow: {
    backgroundColor: "#ffffff",
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#f5f9fc",
    },
  },
  totalBox: {
    width: "380px",
    marginLeft: "auto",
    marginTop: "30px",
    border: "1px solid #e1e8ed",
    borderRadius: "10px",
    padding: "20px 25px",
    background: "#fcfdfe",
    boxShadow: "0 5px 15px rgba(0,0,0,0.03)",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontSize: "15px",
    color: "#5f7c8d",
  },
  grandTotal: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "20px",
    fontWeight: 800,
    color: "#2c3e50",
    borderTop: "2px dashed #dcdcdc",
    paddingTop: "18px",
    marginTop: "15px",
  },
  grandTotalLabel: {
    color: "#34495e",
  },
  grandTotalValue: {
    color: "#e74c3c",
  },
  notice: {
    fontSize: "13px",
    color: "#7f8c8d",
    marginTop: "40px",
    lineHeight: "1.8",
    borderTop: "1px solid #ecf0f1",
    paddingTop: "20px",
  },
  noticeStrong: {
    color: "#34495e",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "15px",
    marginTop: "40px",
  },
  button: {
    padding: "12px 22px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "15px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  printBtn: {
    border: "1px solid #3498db",
    background: "#fff",
    color: "#3498db",
    "&:hover": {
      background: "#eaf2f8",
      color: "#2980b9",
    },
  },
  downloadBtn: {
    background: "#e74c3c",
    color: "#fff",
    border: "none",
    "&:hover": {
      background: "#c0392b",
      boxShadow: "0 4px 15px rgba(231, 76, 60, 0.3)",
    },
  },
};

const districtsData = JSON.parse(localStorage.getItem("districts") || "[]");
const savedCities = JSON.parse(localStorage.getItem("cities") || "[]");
const statesData = JSON.parse(localStorage.getItem("states") || "[]");

const InvoicePage = () => {
  const { razorpayOrderId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const printRef = useRef(); // Create a ref for the element to print

  useEffect(() => {
    fetchInvoice();
  }, [razorpayOrderId]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.post(API_URL, {
        razorpay_orderid: razorpayOrderId,
      });
      setInvoice(res.data.data);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  };

  if (!invoice) return <div style={styles.page}>Loading invoice...</div>;

  const { customer, products } = invoice;

  const subtotal = products.reduce(
    (sum, p) => sum + p.count * Number(p.unit_price),
    0,
  );
  const total = Number(customer.payment_amount);
  const gst = total - subtotal;

  const format = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(v);

  const handlePrint = () => {
    const printContent = printRef.current; // Get the content of the card
    const originalContents = document.body.innerHTML; // Store original body content

    // Temporarily replace body content with only the invoice
    document.body.innerHTML = printContent.innerHTML;

    // Apply print-specific styles
    const printStyles = `
      <style>
        body { margin: 0; padding: 0; }
        .invoice-actions { display: none !important; } /* Hide actions when printing */
        /* You might want to fine-tune these for better print appearance */
        .invoice-card {
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 20px !important;
            max-width: 100% !important;
            margin: 0 !important;
        }
        /* Add other necessary print styles here */
        h1, h2, h3, h4, h5, h6 { color: #000 !important; }
        p, span { color: #333 !important; }
        table { border-collapse: collapse !important; width: 100% !important; }
        th, td { border: 1px solid #eee !important; padding: 8px 10px !important; font-size: 11px !important; }
        th { background-color: #f0f0f0 !important; color: #000 !important; }
        .grand-total-value { color: #000 !important; } /* Ensure total is black */
        .logo-img { filter: none !important; width: 180px !important; }
        .address-block { background: none !important; box-shadow: none !important; }
        /* Hide Header and Footer if they are directly included in the original DOM and not handled by their own print media queries */
        .app-header, .app-footer { display: none !important; }
      </style>
    `;
    document.head.insertAdjacentHTML("beforeend", printStyles);

    window.print(); // Trigger print dialog

    // Restore original body content and remove temporary styles after printing
    document.body.innerHTML = originalContents;
    window.location.reload(); // A simple way to restore the page state
  };

  const handleDownload = () => {
    const invoice = document.getElementById("invoice-card"); // Your invoice div
    if (!invoice) return;

    html2canvas(invoice, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4"); // Portrait, mm, A4

      // Calculate width & height
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice_${new Date().getTime()}.pdf`);
    });
  };
  return (
    <>
      <Helmet>
        <title>Invoice | CGRMART</title>

        <meta
          name="description"
          content="View and download your CGRMART invoice. Secure payment details, order summary, and billing information."
        />
        
        <meta
          name="keywords"
          content="EV charger invoice, Tucker invoice, electric vehicle charger bill, download invoice"
        />

        <meta name="author" content="CGRMART" />

        {/* Open Graph (for WhatsApp, Facebook sharing) */}
        <meta property="og:title" content="Invoice | CGRMART" />
        <meta
          property="og:description"
          content="Access your EV charger invoice and download PDF instantly."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Invoice | CGRMART" />
        <meta
          name="twitter:description"
          content="Download your EV charger invoice securely."
        />

        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header /> {/* Keep Header */}
      <style>{`
        @media (max-width: 1024px) {
          .invoice-card { max-width: 95% !important; padding: 30px 40px !important; }
          .invoice-header { flex-direction: column !important; align-items: flex-start !important; gap: 20px !important; }
          .invoice-info { text-align: left !important; }
          .invoice-address-row { flex-direction: column !important; gap: 20px !important; }
          .invoice-address-block { width: 100% !important; }
          .invoice-table-wrapper { overflow-x: auto !important; }
          .invoice-table { min-width: 600px !important; }
          .invoice-total-box { width: 100% !important; }
        }
        @media (max-width: 768px) {
          .invoice-card { padding: 25px 20px !important; }
          .invoice-logo { width: 180px !important; }
          .invoice-id { font-size: 20px !important; }
          .invoice-date { font-size: 13px !important; }
          .invoice-table { font-size: 12px !important; }
          .invoice-th, .invoice-td { padding: 10px 12px !important; font-size: 12px !important; }
          .invoice-total-box { padding: 15px 20px !important; }
          .invoice-actions { flex-direction: column !important; }
          .invoice-actions button { width: 100% !important; }
        }
        @media (max-width: 480px) {
          .invoice-card { padding: 20px 15px !important; }
          .invoice-logo { width: 150px !important; }
          .invoice-id { font-size: 18px !important; }
          .invoice-table { min-width: 500px !important; font-size: 11px !important; }
          .invoice-th, .invoice-td { padding: 8px 10px !important; font-size: 11px !important; }
        }
      `}</style>
      <div id="invoice-card" style={styles.page}>
        {/* Assign the ref to the element you want to print */}
        <div style={styles.card} ref={printRef} className="invoice-card">
          {" "}
          {/* Added className for targeted CSS */}
          {/* HEADER */}
          <div style={styles.header} className="invoice-header">
            <img
              src="/logo_tucker.png"
              alt="CGRMART Logo"
              style={styles.logo}
              className="logo-img invoice-logo"
            />{" "}
            {/* Added className */}
            <div style={styles.invoiceInfo} className="invoice-info">
              <h4 style={styles.invoiceId} className="invoice-id">
                Invoice #{customer.razorpay_orderid}
              </h4>
              <p style={styles.invoiceDate} className="invoice-date">
                Date: {new Date(customer.order_time).toLocaleDateString()}
              </p>
            </div>
          </div>
          {/* FROM / TO */}
          <div style={styles.addressRow} className="invoice-address-row">
            <div
              style={styles.addressBlock}
              className="address-block invoice-address-block"
            >
              {" "}
              {/* Added className */}
              <h5 style={styles.addressTitle}>From:</h5>
              <p style={styles.addressText}>
                <strong style={styles.strongText}>CGRMART</strong>
                <br />
                Madurai, Tamil Nadu
                <br />
                India
                <br />
                Phone: +91 90000 00000
                <br />
                Email: support@tucker.com
              </p>
            </div>

            <div
              style={{ ...styles.addressBlock, textAlign: "right" }}
              className="address-block invoice-address-block"
            >
              <h5 style={styles.addressTitle}>To:</h5>
              <p style={styles.addressText}>
                <strong style={styles.strongText}>{customer.name}</strong>
                <br />
                {customer.address}
                <br />
                {
                  savedCities.find(
                    (city) => String(city.id) === String(customer.city),
                  )?.name
                }{" "}
                {customer.pincode}
                <br />
                Phone: {customer.mobile}
                <br />
                Email: {customer.email}
              </p>
            </div>
          </div>
          {/* TABLE */}
          <div style={styles.tableWrapper} className="invoice-table-wrapper">
            <table style={styles.table} className="invoice-table">
              <thead>
                <tr>
                  <th style={styles.th} className="invoice-th">
                    ID
                  </th>
                  <th style={styles.th} className="invoice-th">
                    Product
                  </th>
                  <th
                    style={{ ...styles.th, ...styles.tdCenter }}
                    className="invoice-th"
                  >
                    Qty
                  </th>
                  <th
                    style={{ ...styles.th, ...styles.tdRight }}
                    className="invoice-th"
                  >
                    Unit Price (GST Included)
                  </th>
                  <th
                    style={{ ...styles.th, ...styles.tdRight }}
                    className="invoice-th"
                  >
                    Total (GST Included)
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.orderid} style={styles.tbodyRow}>
                    <td style={styles.td} className="invoice-td">
                      {p.orderid}
                    </td>
                    <td
                      style={{ ...styles.td, ...styles.productName }}
                      className="invoice-td"
                    >
                      {p.proname}
                    </td>
                    <td
                      style={{ ...styles.td, ...styles.tdCenter }}
                      className="invoice-td"
                    >
                      {p.count}
                    </td>
                    <td
                      style={{ ...styles.td, ...styles.tdRight }}
                      className="invoice-td"
                    >
                      {format(p.unit_price)}
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        ...styles.tdRight,
                        fontWeight: 700,
                        color: "#2c3e50",
                      }}
                      className="invoice-td"
                    >
                      {format(p.count * p.unit_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* TOTALS */}
          <div style={styles.totalBox} className="invoice-total-box">
            <div style={styles.totalRow}>
              <span>Subtotal (GST Included):</span>
              <span style={styles.strongText}>{format(subtotal)}</span>
            </div>
            <div style={styles.totalRow}>
              <span>Shipping Chargers:</span>
              <span style={styles.strongText}>{format(gst)}</span>
            </div>
            <div style={styles.grandTotal}>
              <span style={styles.grandTotalLabel}>
                Grand Total (GST Included):
              </span>
              <span
                style={{
                  ...styles.grandTotalValue,
                  className: "grand-total-value",
                }}
              >
                {format(total)}
              </span>{" "}
              {/* Added className */}
            </div>
          </div>
          {/* NOTICE */}
          <p style={styles.notice}>
            <strong style={styles.noticeStrong}>Notice:</strong> This is a
            computer generated invoice and does not require a physical
            signature. Thank you for your business!
          </p>
          {/* ACTIONS (will be hidden using CSS injected by handlePrint) */}
          <div style={styles.actions} className="invoice-actions">
            {" "}
            {/* Added className */}
            <button
              style={{ ...styles.button, ...styles.printBtn }}
              onClick={handlePrint}
            >
              <span role="img" aria-label="print">
                🖨
              </span>{" "}
              Print Invoice
            </button>
            <button
              style={{ ...styles.button, ...styles.downloadBtn }}
              onClick={handleDownload}
            >
              <span role="img" aria-label="download">
                ⬇
              </span>{" "}
              Download PDF
            </button>
          </div>
        </div>
      </div>
      <Footer /> {/* Keep Footer */}
    </>
  );
};

export default InvoicePage;
